import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { last, without } from 'lodash';

import TitleContext from './TitleContext';

/** @returns {import('./TitleContext').TitleObject[]} */
const createInitialState = () => [];

export default function TitleManager({ default: defaultTitle = '', tabTitleTail, children }) {
    const [registered, setRegistered] = useState(createInitialState);

    const register = useCallback(title => {
        // Convert to TitleObject if not one already
        const obj = typeof title === 'object' ? title : { title };

        setRegistered(prev => {
            return [...prev, obj]
                .sort((a, b) => (a.priority ?? 0) - (b.priority ?? 0));
        });

        return () => setRegistered(prev => without(prev, obj));
    }, []);

    const subject = last(registered);

    // Title shown in-app
    const title = useMemo(() => {
        if (!subject) return defaultTitle;

        return subject.title
            ?? subject.breadcrumbs?.join(' - ')
            ?? defaultTitle;
    }, [subject, defaultTitle]);

    // Breadcrumbs for use i.e. in tab title
    const breadcrumbs = useMemo(() => {
        if (!subject) return null;

        return subject.breadcrumbs ?? [subject.title];
    }, [subject]);

    // Update tab title w/ breadcrumbs
    useEffect(() => {
        if (breadcrumbs) {
            const finalBreadcrumbs = [...breadcrumbs].reverse();
            if (tabTitleTail) {
                finalBreadcrumbs.push(tabTitleTail);
            }

            document.title = finalBreadcrumbs?.join(' - ');
        }
        else {
            document.title = defaultTitle;
        }
    }, [breadcrumbs, defaultTitle]);

    const contextObject = useMemo(() => ({
        title,
        register
    }), [title, register]);

    return (
        <TitleContext.Provider value={contextObject}>
            {children}
        </TitleContext.Provider>
    )
}